import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { AmountSelectorComponent } from '../../shared/form-controls/amount-selector.component';
import { DonationTypeToggleComponent } from '../../shared/form-controls/donation-type-toggle.component';

import { PriceEnvelope } from '../../../types/stripe-models';
import { selectSelectedProductId } from '../../../state/campaing/selectors';

import {
  DONATION_PLANS,
  PlanId,
  PriceFormValue,
  PriceInput,
} from '../../../types/donation-plans';
import { buildPriceRequest } from '../../../types/build-price-request';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'don-price-form',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DonationTypeToggleComponent,
        AmountSelectorComponent,
        TranslateModule,
    ],
    templateUrl: './price-form.component.html',
    styleUrls: ['./price-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceFormComponent implements OnChanges {
  private readonly defaultAmountsByPlan: Record<PlanId, number> = {
    monthly: 25,
    'one-time': 55,
  };

  /** Viene del store (selector combinado) */
  @Input() price!: PriceInput;

  /** Si es false, sólo se permite one-time */
  @Input() canAcceptRecurring = false;

  @Output() submitted = new EventEmitter<PriceEnvelope>();

  /** Planes centralizados (inyectados) */
  private readonly _plans = inject(DONATION_PLANS);
  public get plans() {
    return this._plans;
  }

  /** Form fuertemente tipado */
  form: FormGroup<{
    donationType: FormControl<PlanId | null>;
    unitAmount: FormControl<number | null>;
    nickname: FormControl<string>;
    currency: FormControl<string>;
  }>;

  constructor(private fb: FormBuilder, private store: Store) {
    const defaultPlan = this.getPreferredPlanId();

    this.form = this.fb.nonNullable.group({
      donationType: new FormControl<PlanId | null>(
        defaultPlan,
        {
          nonNullable: false,
          validators: [Validators.required],
        }
      ),
      unitAmount: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.min(1)],
      }),
      nickname: new FormControl<string>('Default Price', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      currency: new FormControl<string>('USD', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    this.syncDefaultAmount(this.form.get('donationType')!.value);

    this.form.get('donationType')!.valueChanges.subscribe((planId) => {
      this.syncDefaultAmount(planId);
    });

    // Validador: si no aceptas recurring, no permitas planes con recurring
    this.form.get('donationType')!.addValidators((ctrl) => {
      const id = ctrl.value;
      if (!id) return { required: true };
      if (!this.canAcceptRecurring) {
        const plan = this.plans.find((p) => p.id === id);
        if (plan && plan.recurring) return { recurringNotAllowed: true };
      }
      return null;
    });
  }

  /** Para el template: deshabilita botones recurrentes si no se permiten */
  get disabledPlanIds(): PlanId[] {
    if (this.canAcceptRecurring) return [];
    return this.plans.filter((p) => !!p.recurring).map((p) => p.id) as PlanId[];
  }

  /** Parchea valores entrantes sin tocar `recurring` a mano */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['price']?.currentValue) {
      const p = changes['price'].currentValue as PriceInput;

      const cur = (p.currency ?? 'USD').toUpperCase();

      const patch: Partial<PriceFormValue> = {
        nickname: p.nickname ?? 'Default Price',
        currency: cur,
      };

      // unitAmount: convertir de minor units (string) → major (number)
      const decimals = this.getCurrencyDecimals(cur);
      if (typeof p.unitAmount === 'string') {
        const majorAmount = Number(p.unitAmount) / Math.pow(10, decimals);
        if (!isNaN(majorAmount) && isFinite(majorAmount)) {
          patch.unitAmount = majorAmount;
        }
      }

      // donationType → mapeo a PlanId
      let incoming: PlanId | undefined;

      if (p.donationType === 'recurring') {
        // ejemplo: interval: 'month' → monthly
        const interval = p.recurring?.interval ?? 'month';
        if (interval === 'month') {
          incoming = 'monthly' as PlanId;
        }
        // 👇 si quieres soportar más intervals en el futuro
        // if (interval === 'year') incoming = 'yearly' as PlanId;
      } else {
        incoming = p.donationType as PlanId;
      }

      if (incoming && this.plans.some((pl) => pl.id === incoming)) {
        patch.donationType = incoming;
      }

      this.form.patchValue(patch, { emitEvent: false });

      const normalizedPlanId = this.getPreferredPlanId(
        this.form.get('donationType')!.value
      );
      if (normalizedPlanId !== this.form.get('donationType')!.value) {
        this.form.patchValue({ donationType: normalizedPlanId }, { emitEvent: false });
      }

      this.form
        .get('donationType')!
        .updateValueAndValidity({ emitEvent: false });

      this.syncDefaultAmount(this.form.get('donationType')!.value);
    }

    // Revalida si cambia el flag canAcceptRecurring
    if (
      changes['canAcceptRecurring'] &&
      !changes['canAcceptRecurring'].firstChange
    ) {
      const nextPlanId = this.getPreferredPlanId(this.form.get('donationType')!.value);
      if (nextPlanId !== this.form.get('donationType')!.value) {
        this.form.patchValue({ donationType: nextPlanId }, { emitEvent: false });
        this.form.get('donationType')!.markAsPristine();
      }

      this.syncDefaultAmount(this.form.get('donationType')!.value);
      this.form.get('donationType')!.updateValueAndValidity();
    }
  }

  /** Texto de error del control donationType */
  get donationTypeError(): string {
    const e = this.form.get('donationType')?.errors;
    if (!e) return '';
    if (e['required']) return 'Please select a donation type.';
    if (e['recurringNotAllowed'])
      return 'Recurring donations are not allowed for this campaign.';
    return 'Invalid donation type.';
  }

  /** Manejo de selección rápida de montos (chips) */
  onAmountSelect(amount: number) {
    this.form.patchValue({ unitAmount: amount });
    this.form.get('unitAmount')?.markAsDirty();
    this.form.get('unitAmount')?.markAsTouched();
  }

  /** Submit absolutamente determinístico a partir del plan */
  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const v = this.form.value as PriceFormValue;

    const plan = this.plans.find((p) => p.id === v.donationType!);
    if (!plan) return; // sanity-check

    this.store
      .select(selectSelectedProductId)
      .pipe(take(1))
      .subscribe((productId) => {
        const req = buildPriceRequest({
          productId: productId ?? undefined,
          nickname: v.nickname,
          currencyUpper: (v.currency || 'USD').toUpperCase(),
          unitAmountMajor: v.unitAmount ?? 0,
          plan,
        });

        const envelope: PriceEnvelope = { type: 'Price', data: req };
        this.submitted.emit(envelope);
      });
  }

  // ===== helpers de moneda =====
  private getCurrencyDecimals(currencyUpper: string): number {
    const ZERO_DEC = new Set(['JPY', 'KRW', 'CLP']);
    return ZERO_DEC.has(currencyUpper) ? 0 : 2;
  }

  private syncDefaultAmount(planId: PlanId | null): void {
    if (!planId) {
      return;
    }

    const control = this.form.get('unitAmount');
    if (!control) {
      return;
    }

    const currentValue = control.value;
    if (currentValue !== null && currentValue !== undefined && currentValue > 0) {
      return;
    }

    const defaultAmount = this.defaultAmountsByPlan[planId];
    if (!defaultAmount) {
      return;
    }

    control.setValue(defaultAmount, { emitEvent: false });
  }

  private getPreferredPlanId(currentPlanId?: PlanId | null): PlanId {
    if (currentPlanId) {
      const currentPlan = this.plans.find((plan) => plan.id === currentPlanId);
      if (currentPlan && (this.canAcceptRecurring || !currentPlan.recurring)) {
        return currentPlanId;
      }
    }

    const defaultPlan = this.plans.find(
      (plan) => plan.default && (this.canAcceptRecurring || !plan.recurring)
    );
    if (defaultPlan) {
      return defaultPlan.id as PlanId;
    }

    const firstAllowedPlan = this.plans.find(
      (plan) => this.canAcceptRecurring || !plan.recurring
    );

    return (firstAllowedPlan?.id ?? 'one-time') as PlanId;
  }
}
